import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { OrganizationsService } from "../services/organizationsService";
import type { CreateOrganizationRequest } from "../types/organization-form.types";
import type {
  Organization,
  OrganizationStatus as OrganizationStatusEnum,
} from "../types/organizations-api.types";
import { OrganizationStatus } from "../types/organizations-api.types";
import type { OrganizationsPagination } from "../types/organizations.types";
import { transformOrganizationToTableRow } from "../utils/organizationHelpers";

export interface SelectOption {
  label: string;
  value: string;
}

export const useOrganizationsPage = () => {
  const { t } = useTranslations("cards");
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
  const initialPerPage = 10;

  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialPerPage);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [pagination, setPagination] = useState<OrganizationsPagination | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
    if (pageFromUrl !== currentPage) {
      setCurrentPage(pageFromUrl);
    }
  }, [searchParams, currentPage]);

  const fetchOrganizations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const limit = itemsPerPage;
      const skip = (currentPage - 1) * limit;

      const filters = {
        limit,
        skip,
        status: statusFilter ? (statusFilter as OrganizationStatusEnum) : undefined,
      };

      const response = await OrganizationsService.fetchOrganizations(filters);
      const responseData = response.data;

      let organizationsData: Organization[] = [];
      let paginationData: OrganizationsPagination | null = null;

      if (responseData.data && Array.isArray(responseData.data)) {
        organizationsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        organizationsData = responseData;
      }

      if (responseData.total !== undefined) {
        const total = responseData.total;
        const responseLimit = responseData.limit || limit;
        const responseSkip = responseData.skip !== undefined ? responseData.skip : skip;

        const calculatedCurrentPage = Math.floor(responseSkip / responseLimit) + 1;
        const calculatedLastPage = Math.ceil(total / responseLimit) || 1;

        paginationData = {
          current_page: calculatedCurrentPage,
          per_page: responseLimit,
          total,
          last_page: calculatedLastPage,
          from: responseSkip + 1,
          to: Math.min(responseSkip + responseLimit, total),
        };
      } else {
        const total = organizationsData.length;
        paginationData = {
          current_page: currentPage,
          per_page: limit,
          total,
          last_page: Math.ceil(total / limit) || 1,
          from: skip + 1,
          to: Math.min(skip + limit, total),
        };
      }

      setOrganizations(organizationsData);
      setPagination(paginationData);
    } catch (err: unknown) {
      const errorMessage = HttpApiService.getErrorMessage(err) || t("organizations_error_load");
      setError(errorMessage);
      setOrganizations([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, statusFilter, t]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const paginationOptions: SelectOption[] = [
    { label: "10", value: "10" },
    { label: "25", value: "25" },
    { label: "50", value: "50" },
    { label: "100", value: "100" },
  ];

  const statusFilterOptions: SelectOption[] = [
    { label: "Seleccionar", value: "all" },
    { label: "Activo", value: OrganizationStatus.ACTIVE },
    { label: "Inactivo", value: OrganizationStatus.INACTIVE },
    { label: "Suspendido", value: OrganizationStatus.SUSPENDED },
    { label: "Pendiente", value: OrganizationStatus.PENDING },
  ];

  const tableRows = useMemo(() => {
    return organizations.map(transformOrganizationToTableRow);
  }, [organizations]);

  const totalItems = pagination?.total || organizations.length;
  const lastPage = pagination?.last_page || Math.ceil(totalItems / itemsPerPage) || 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < lastPage;

  const getPageNumbers = useCallback(() => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 7;

    if (lastPage <= maxPagesToShow) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(lastPage);
      } else if (currentPage >= lastPage - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = lastPage - 3; i <= lastPage; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(lastPage);
      }
    }

    return pages;
  }, [currentPage, lastPage]);

  const pageNumbers = useMemo(() => getPageNumbers(), [getPageNumbers]);

  const updateUrlParams = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      if (page === 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | null) => {
      setStatusFilter(value);

      if (currentPage !== 1) {
        setCurrentPage(1);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("page");
        setSearchParams(newParams, { replace: true });
      }
    },
    [currentPage, searchParams, setSearchParams],
  );

  const handleItemsPerPageChange = useCallback(
    (value: string) => {
      const newPerPage = parseInt(value, 10);
      if (isNaN(newPerPage)) return;

      setItemsPerPage(newPerPage);
      if (currentPage !== 1) {
        setCurrentPage(1);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("page");
        setSearchParams(newParams, { replace: true });
      } else {
        setCurrentPage(1);
      }
    },
    [currentPage, searchParams, setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateUrlParams(page);
    },
    [updateUrlParams],
  );

  const handleNewOrganizationClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateOrganization = useCallback(
    async (data: CreateOrganizationRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        await OrganizationsService.createOrganization(data);
        colocaToast.success(t("organizations_success_create"));
        setIsCreateModalOpen(false);
        await fetchOrganizations();
      } catch (err: unknown) {
        const errorMessage = HttpApiService.getErrorMessage(err) || t("organizations_error_create");
        setError(errorMessage);
        throw err; // Re-lanzar para que el modal maneje el error
      } finally {
        setIsLoading(false);
      }
    },
    [fetchOrganizations, t],
  );

  return {
    organizations: tableRows,
    totalItems,
    lastPage,
    currentPage,
    itemsPerPage,
    canGoPrevious,
    canGoNext,
    pageNumbers,
    paginationOptions,
    statusFilterValue: statusFilter,
    statusFilterOptions,
    onStatusFilterChange: handleStatusFilterChange,
    isLoading,
    error,
    handleItemsPerPageChange,
    handlePageChange,
    handleNewOrganizationClick,
    handleCreateOrganization,
    isCreateModalOpen,
    setIsCreateModalOpen,
    pagination,
    fetchOrganizations,
  };
};
